import { useState } from 'react';
import { usePermission } from '../../../../hooks/usePermission';
import { useMembershipStore } from '../../../../stores/membershipStore';
import { fmtDate } from './utils';

interface Props {
  /** 点击「立即升级 / 续费延期」时打开结算弹窗 */
  onUpgrade: () => void;
  /** 外部传入的提示消息（如支付结果） */
  doneMsg?: string;
}

/**
 * 会员状态卡片（顶部 hero）：展示当前等级、到期时间、剩余天数、自动续费开关与升级入口。
 * 数据来自服务端（usePermission → membershipStore → GET /membership/status），不再前端硬编码。
 */
export function MembershipStatusCard({ onUpgrade, doneMsg }: Props) {
  const permission = usePermission();
  const setAutoRenew = useMembershipStore((s) => s.setAutoRenew);
  const [toggling, setToggling] = useState(false);
  const [localMsg, setLocalMsg] = useState('');

  const { isPremium, isExpired, expireAt, daysLeft, levelName, autoRenew, isAdmin } = permission;
  const msg = localMsg || doneMsg || '';
  // 管理员对会员等级没有任何权限差异：给他摆 VIP 标、到期时间和「立即升级」，
  // 等于推销一件他不需要买的东西（与设置页入口收口、两端 App 副标题同一口径）。
  const showPremium = isPremium && !isAdmin;

  const handleToggleAutoRenew = async () => {
    if (toggling) return;
    setToggling(true);
    setLocalMsg('');
    const res = await setAutoRenew(!autoRenew);
    setToggling(false);
    if (!res.ok) setLocalMsg(res.message ?? '操作失败');
  };

  return (
    <div
      className={`rounded-xl p-5 border ${
        showPremium ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200' : 'bg-gray-50 border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${
              showPremium ? 'bg-amber-100' : 'bg-gray-200'
            }`}
          >
            <i
              className={`${
                isAdmin
                  ? 'ri-shield-star-line text-gray-600'
                  : showPremium
                    ? 'ri-vip-crown-2-fill text-amber-500'
                    : 'ri-user-line text-gray-500'
              } text-xl`}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-semibold text-gray-900">
                {isAdmin ? '管理员' : levelName || '加载中…'}
              </span>
              {showPremium && (
                <span className="text-xs px-2 py-0.5 bg-amber-500 text-white rounded-full font-medium whitespace-nowrap">VIP</span>
              )}
              {isAdmin && (
                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full font-medium whitespace-nowrap">全部功能</span>
              )}
              {isExpired && !isAdmin && (
                <span className="text-xs px-2 py-0.5 bg-rose-100 text-rose-600 rounded-full font-medium whitespace-nowrap">已过期</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {isAdmin
                ? '已拥有全部功能，无需开通会员'
                : showPremium && expireAt
                  ? `有效期至 ${fmtDate(expireAt)}${daysLeft != null ? `（剩余 ${daysLeft} 天）` : ''}`
                  : showPremium
                    ? '长期有效'
                    : isExpired && expireAt
                      ? `已于 ${fmtDate(expireAt)} 到期`
                      : '基础功能版，升级后解锁全部高级功能'}
            </p>
          </div>
        </div>

        {!isAdmin && (
          <button
            type="button"
            onClick={onUpgrade}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              showPremium ? 'bg-amber-100 hover:bg-amber-200 text-amber-700' : 'bg-amber-500 hover:bg-amber-600 text-white'
            }`}
          >
            <i className={`${showPremium ? 'ri-refresh-line' : 'ri-vip-crown-line'} text-sm`} />
            {showPremium ? '续费延期' : '立即升级'}
          </button>
        )}
      </div>

      {/* 自动续费开关：仅付费会员可设置（后端对免费等级会拒绝） */}
      {showPremium && (
        <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-amber-100">
          <div className="flex items-center gap-2">
            <i className="ri-repeat-line text-amber-500 text-sm" />
            <span className="text-xs text-amber-700">自动续费</span>
            <span className="text-xs text-amber-500/70">（到期前自动续订，可随时关闭）</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={autoRenew}
            disabled={toggling}
            onClick={handleToggleAutoRenew}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
              autoRenew ? 'bg-amber-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                autoRenew ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      )}

      {msg && (
        <div
          className={`mt-3 text-xs px-3 py-2 rounded-lg ${
            msg.includes('失败') ? 'bg-rose-50 text-rose-600' : 'bg-teal-50 text-teal-600'
          }`}
        >
          <i className={`${msg.includes('失败') ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} mr-1`} />
          {msg}
        </div>
      )}
    </div>
  );
}
